import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useRef } from 'react';
import { ArrowRight, CheckCircle, MapPin, Phone, Airplane, ShieldCheck, Globe, Users } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { TrustBar, StatsSection, SectionHeader } from '@/components/common/Sections';
import { ServiceCard, TestimonialCard, CompanyCard, CTABanner } from '@/components/common/Cards';
import SEO from '@/components/common/SEO';
import { useCMS } from '@/lib/useCMS';
import {
  COMPANY_INFO,
  STATISTICS,
  SERVICES,
  GROUP_COMPANIES,
  OFFICES,
  IMAGES,
  LEADERSHIP,
} from '@/lib/constants';

export default function HomePage() {
  const { testimonials, companyInfo } = useCMS();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  const highlights = [
    { icon: ShieldCheck, label: "Dual IATA Accredited" },
    { icon: Globe, label: "18 India + 9 International Offices" },
    { icon: Users, label: "150+ Travel Experts" },
    { icon: Airplane, label: "1M+ Happy Travelers" },
  ];

  return (
    <div className="page-transition">
      <SEO
        title="Home"
        description="Osaka Group — Kerala's premier travel management company since 1993. Dual IATA accredited. Visa services, air ticketing, forex, tour packages, corporate travel & study abroad across 27 offices worldwide."
        path="/"
      />
      <section
        ref={heroRef}
        data-testid="hero-section"
        className="relative h-screen flex items-center overflow-hidden"
      >
        {/* Parallax Background */}
        <motion.div className="absolute inset-0" style={{ y: heroY }}>
          <img
            src={IMAGES.hero}
            alt="Airplane taking off at sunset"
            className="w-full h-[120%] object-cover"
          />
        </motion.div>

        {/* Gradient overlays for depth */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1F3D]/95 via-[#0A1F3D]/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F3D] via-transparent to-[#0A1F3D]/30" />

        {/* Content */}
        <motion.div
          className="relative z-10 container-custom"
          style={{ opacity: heroOpacity }}
        >
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              className="flex items-center gap-3 mb-8"
            >
              <div className="w-12 h-[1px] bg-[#D4AF37]" />
              <span className="text-xs font-bold tracking-[0.25em] uppercase text-[#D4AF37]">
                Since 1993
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="text-4xl sm:text-5xl lg:text-[72px] font-bold font-['Playfair_Display'] text-white leading-[1.08] tracking-tight mb-6"
            >
              Where Every Journey
              <br />
              Begins with{" "}
              <span className="relative inline-block">
                <span className="text-[#D4AF37]">Trust</span>
                <motion.span
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.6, delay: 0.9 }}
                  className="absolute -bottom-2 left-0 w-full h-[3px] bg-[#D4AF37] origin-left"
                />
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="text-lg text-white/70 mb-10 max-w-lg leading-relaxed"
            >
              From Kerala to the world — three decades of turning travel dreams into 
              boarding passes. Visa assistance, curated holidays, corporate travel 
              & beyond.
            </motion.p>

            {/* CTA Row */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-4 mb-14"
            >
              <Link to="/contact">
                <Button
                  data-testid="hero-contact-btn"
                  className="bg-[#D4AF37] text-[#0A1F3D] hover:bg-[#c5a033] px-8 py-6 text-base font-semibold rounded-none group"
                >
                  Plan Your Journey
                  <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/services">
                <Button
                  data-testid="hero-services-btn"
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 hover:border-white/40 px-8 py-6 text-base rounded-none"
                >
                  Explore Services
                </Button>
              </Link>
            </motion.div>

            {/* Highlight badges */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="flex flex-wrap gap-6"
            >
              {highlights.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 + i * 0.1 }}
                  className="flex items-center gap-2"
                >
                  <item.icon size={18} weight="bold" className="text-[#D4AF37]" />
                  <span className="text-white/60 text-sm">{item.label}</span>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        >
          <span className="text-white/40 text-[10px] tracking-[0.3em] uppercase">Scroll</span>
          <div className="w-[1px] h-8 relative overflow-hidden">
            <motion.div
              animate={{ y: ["-100%", "100%"] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
              className="w-full h-full bg-[#D4AF37]"
            />
          </div>
        </motion.div>
      </section>

      {/* ===== TRUST BAR ===== */}
      <TrustBar />

      {/* ===== ABOUT / WHO WE ARE ===== */}
      <section data-testid="about-preview" className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            {/* Text column */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-[1px] bg-[#D4AF37]" />
                <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#D4AF37]">
                  Who We Are
                </span>
              </div>

              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-['Playfair_Display'] text-[#0A1F3D] mb-6 tracking-tight leading-tight">
                Three Decades of<br />
                Making <span className="text-[#D4AF37]">Impossible</span> Trips Possible
              </h2>

              <div className="gold-line mb-8" />

              <p className="text-[#4A5568] leading-relaxed mb-5 text-base">
                What started in 1993 as a small travel desk in Angamaly has grown into one of India's most 
                respected travel conglomerates — with five specialized companies, eight offices across 
                India & the UK, and over a million travelers who call us family.
              </p>
              <p className="text-[#4A5568] leading-relaxed mb-10 text-base">
                We don't just book tickets. We architect journeys — from visa documentation 
                to corporate travel management, study abroad placements to curated holiday 
                packages that create stories worth telling.
              </p>

              {/* Mini stats row */}
              <div className="grid grid-cols-3 gap-6 mb-10 pb-10 border-b border-slate-100">
                {[
                  { num: "33+", label: "Years" },
                  { num: "5", label: "Companies" },
                  { num: "1M+", label: "Travelers" },
                ].map((s, i) => (
                  <div key={i}>
                    <div className="text-3xl font-bold font-['Playfair_Display'] text-[#0A1F3D]">{s.num}</div>
                    <div className="text-sm text-[#4A5568]">{s.label}</div>
                  </div>
                ))}
              </div>

              <Link to="/about">
                <Button
                  data-testid="about-learn-more-btn"
                  variant="outline"
                  className="border-[#0A1F3D] text-[#0A1F3D] hover:bg-[#0A1F3D] hover:text-white px-6 py-5 rounded-none group"
                >
                  Our Full Story
                  <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </motion.div>

            {/* Image column — stacked composition */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="relative"
            >
              <div className="relative">
                {/* Main image */}
                <div className="img-zoom">
                  <img
                    src={IMAGES.corporateTravel}
                    alt="Premium Travel Experience"
                    className="w-full aspect-[4/3] object-cover"
                  />
                </div>

                {/* Overlay accent card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5 }}
                  className="absolute -bottom-6 -left-2 md:-bottom-10 md:-left-10 bg-[#0A1F3D] p-5 md:p-8 shadow-2xl"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 border border-[#D4AF37]/30 flex items-center justify-center">
                      <ShieldCheck size={28} className="text-[#D4AF37]" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold font-['Playfair_Display'] text-[#D4AF37]">Dual IATA</div>
                      <div className="text-sm text-white/60">Accredited Agency</div>
                    </div>
                  </div>
                </motion.div>

                {/* Gold accent border */}
                <div className="absolute -top-4 -right-4 w-full h-full border-2 border-[#D4AF37]/20 -z-10" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== STATISTICS ===== */}
      <StatsSection stats={STATISTICS} dark />

      {/* ===== SERVICES ===== */}
      <section data-testid="services-section" className="section-padding bg-[#F5F5F5]">
        <div className="container-custom">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-12 md:mb-16">
            <div className="max-w-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-[1px] bg-[#D4AF37]" />
                <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#D4AF37]">
                  What We Offer
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-['Playfair_Display'] text-[#0A1F3D] tracking-tight">
                Eight Ways to Travel{" "}
                <span className="text-[#D4AF37]">Smarter</span>
              </h2>
            </div>
            <Link to="/services" className="mt-6 lg:mt-0">
              <Button
                data-testid="services-view-all-btn"
                variant="outline"
                className="border-[#0A1F3D] text-[#0A1F3D] hover:bg-[#0A1F3D] hover:text-white px-6 py-5 rounded-none group"
              >
                All Services
                <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES.map((service, index) => (
              <ServiceCard key={service.id} service={service} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* ===== GROUP COMPANIES ===== */}
      <section data-testid="companies-section" className="section-padding bg-white">
        <div className="container-custom">
          <SectionHeader
            overline="The Osaka Ecosystem"
            title="Five Companies, One Vision"
            description="Each brand in our portfolio is a specialist — together, they form the most comprehensive travel network in India."
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {GROUP_COMPANIES.slice(0, 3).map((company, index) => (
              <CompanyCard key={company.id} company={company} index={index} />
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-8 mt-8 max-w-4xl mx-auto">
            {GROUP_COMPANIES.slice(3).map((company, index) => (
              <CompanyCard key={company.id} company={company} index={index + 3} />
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/companies">
              <Button
                data-testid="view-all-companies-btn"
                variant="outline"
                className="border-[#0A1F3D] text-[#0A1F3D] hover:bg-[#0A1F3D] hover:text-white px-8 py-5 rounded-none"
              >
                View All Companies
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== WHY CHOOSE US ===== */}
      <section data-testid="why-choose-section" className="section-padding bg-[#0A1F3D] relative grain-overlay overflow-hidden">
        {/* Background decorative image */}
        <div className="absolute right-0 top-0 w-1/2 h-full opacity-[0.07]">
          <img src={IMAGES.airport} alt="" className="w-full h-full object-cover" />
        </div>

        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left — heading */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-[1px] bg-[#D4AF37]" />
                <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#D4AF37]">
                  Why Choose Us
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-['Playfair_Display'] text-white tracking-tight leading-tight mb-6">
                Not Just Another
                <br />
                Travel Agency
              </h2>
              <p className="text-white/60 text-base leading-relaxed max-w-md mb-10">
                In an industry flooded with options, here's why over a million travelers 
                choose to come back to Osaka, again and again.
              </p>
              <Link to="/about">
                <Button
                  data-testid="why-choose-cta"
                  className="bg-[#D4AF37] text-[#0A1F3D] hover:bg-[#c5a033] px-8 py-6 text-base font-semibold rounded-none"
                >
                  Learn More
                </Button>
              </Link>
            </div>

            {/* Right — advantage cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[
                {
                  num: "01",
                  title: "Dual IATA Accredited",
                  description: "One of the few agencies in Kerala with dual IATA accreditation — your guarantee of reliability.",
                },
                {
                  num: "02",
                  title: "33+ Years of Mastery",
                  description: "Three decades of deep industry relationships and expertise that no newcomer can replicate.",
                },
                {
                  num: "03",
                  title: "150+ Travel Experts",
                  description: "A dedicated team of specialists for every travel vertical, available when you need them.",
                },
                {
                  num: "04",
                  title: "Pan-India + UK",
                  description: "Eight offices strategically placed so help is never more than a phone call away.",
                },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.12 }}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 p-6 hover:bg-white/10 transition-colors duration-300"
                >
                  <span className="text-[#D4AF37] text-sm font-bold font-['Playfair_Display'] block mb-3">
                    {item.num}
                  </span>
                  <h3 className="text-lg font-bold font-['Playfair_Display'] text-white mb-2">
                    {item.title}
                  </h3>
                  <p className="text-white/50 text-sm leading-relaxed">
                    {item.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== LEADERSHIP PREVIEW ===== */}
      <section data-testid="leadership-preview" className="section-padding bg-white">
        <div className="container-custom">
          <SectionHeader
            overline="The People Behind the Vision"
            title="Our Leadership"
            description="Meet the minds that have steered Osaka Group from a single desk to an international travel powerhouse."
          />

          <div className="grid md:grid-cols-3 gap-10 max-w-5xl mx-auto">
            {LEADERSHIP.map((leader, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                data-testid={`leader-card-${index}`}
                className="group text-center"
              >
                <div className="relative overflow-hidden mb-6 aspect-[3/4]">
                  <img
                    src={leader.image}
                    alt={leader.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F3D]/60 via-transparent to-transparent" />
                </div>
                <h3 className="text-xl font-bold font-['Playfair_Display'] text-[#0A1F3D] mb-1">
                  {leader.name}
                </h3>
                <p className="text-[#D4AF37] text-sm font-medium">{leader.designation}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/leadership">
              <Button
                data-testid="view-leadership-btn"
                variant="outline"
                className="border-[#0A1F3D] text-[#0A1F3D] hover:bg-[#0A1F3D] hover:text-white px-8 py-5 rounded-none"
              >
                Meet the Full Team
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== GLOBAL PRESENCE ===== */}
      <section data-testid="global-presence-preview" className="relative overflow-hidden">
        <div className="grid lg:grid-cols-2 min-h-[500px] lg:min-h-[600px]">
          {/* Image half */}
          <div className="relative h-[300px] lg:h-auto">
            <img
              src={IMAGES.dubai}
              alt="Dubai Skyline"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-[#0A1F3D]/40" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  className="text-7xl md:text-8xl font-bold font-['Playfair_Display'] text-[#D4AF37]"
                >
                  27
                </motion.div>
                <div className="text-white text-lg mt-2 tracking-wide">Offices Worldwide</div>
                <div className="text-white/50 text-sm mt-1">18 India + 9 International</div>
              </div>
            </div>
          </div>

          {/* Office list half */}
          <div className="bg-[#0A1F3D] p-6 md:p-10 lg:p-16 flex flex-col justify-center">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-[1px] bg-[#D4AF37]" />
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#D4AF37]">
                Our Presence
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold font-['Playfair_Display'] text-white mb-8 tracking-tight">
              Wherever You Are,<br />
              <span className="text-[#D4AF37]">We're Close</span>
            </h2>

            <div className="space-y-3 mb-10">
              {OFFICES.slice(0, 5).map((office, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08 }}
                  className="flex items-center gap-4 p-4 bg-white/5 border border-white/5 hover:bg-white/10 hover:border-[#D4AF37]/20 transition-colors duration-300 group"
                >
                  <MapPin size={20} className="text-[#D4AF37] flex-shrink-0" />
                  <span className="text-white/80 flex-1 font-medium text-sm">
                    {office.city}
                  </span>
                  <Phone size={16} className="text-white/30 group-hover:text-[#D4AF37] transition-colors" />
                </motion.div>
              ))}
            </div>

            <Link to="/global-presence">
              <Button
                data-testid="view-all-offices-btn"
                variant="outline"
                className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A1F3D] px-6 py-5 rounded-none"
              >
                All Locations
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section data-testid="testimonials-section" className="section-padding bg-[#F5F5F5]">
        <div className="container-custom">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-12 md:mb-16">
            <div className="max-w-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-[1px] bg-[#D4AF37]" />
                <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#D4AF37]">
                  Voices of Trust
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-['Playfair_Display'] text-[#0A1F3D] tracking-tight">
                4,000+ Happy Reviews{" "}
                <span className="text-[#D4AF37]">& Counting</span>
              </h2>
            </div>
            <Link to="/testimonials" className="mt-6 lg:mt-0">
              <Button
                data-testid="view-all-testimonials-btn"
                variant="outline"
                className="border-[#0A1F3D] text-[#0A1F3D] hover:bg-[#0A1F3D] hover:text-white px-6 py-5 rounded-none"
              >
                All Reviews
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.slice(0, 3).map((testimonial, index) => (
              <TestimonialCard key={testimonial.id || index} testimonial={testimonial} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA BANNER ===== */}
      <section data-testid="cta-banner" className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.visa} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0A1F3D]/90" />
        </div>
        <div className="relative z-10 container-custom py-16 md:py-24 lg:py-32">
          <div className="max-w-3xl mx-auto text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl lg:text-5xl font-bold font-['Playfair_Display'] text-white mb-6 tracking-tight"
            >
              Your Next Adventure is<br />
              Just a <span className="text-[#D4AF37]">Conversation</span> Away
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-white/60 text-lg mb-10 max-w-xl mx-auto"
            >
              Whether it's a dream vacation, a business trip, or a life-changing move abroad — 
              let's make it happen together.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to="/contact">
                <Button
                  data-testid="cta-contact-btn"
                  className="bg-[#D4AF37] text-[#0A1F3D] hover:bg-[#c5a033] px-10 py-6 text-base font-semibold rounded-none"
                >
                  Get in Touch
                </Button>
              </Link>
              <a href={`https://wa.me/${companyInfo.whatsapp.replace(/\s/g, '')}`} target="_blank" rel="noopener noreferrer">
                <Button
                  data-testid="cta-whatsapp-btn"
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 px-10 py-6 text-base rounded-none"
                >
                  WhatsApp Us
                </Button>
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
